import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Tooltip } from '@mui/material';
import { FaClipboardList } from '@react-icons/all-files/fa/FaClipboardList';
import { FaPaperclip } from '@react-icons/all-files/fa/FaPaperclip';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaSync } from '@react-icons/all-files/fa/FaSync';
import Breadcrumb from '../../commons/Breadcrumb';
import PageHeader from '../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import ProfaseDataGrid, { ITEMS_PER_PAGE } from '../../commons/Datagrid';
import LinkCell from '../../commons/LinkCell';
import UserInfo from '../../commons/UserInfo';
import DateFormat from '../../commons/componentes/DateFormat';
import { EMPRESA_VENCIMIENTO_DATE_PATTERN, ACTIVIDAD_VENCIDA_DATE_STYLE } from '../../shared/date/date-formats';
import ActividadAdjuntosListModal, {
  buildAdjuntosModalContextFromEstadoNodosRow,
  type ActividadAdjuntosListModalContext,
} from '../../kanban/ActividadAdjuntosListModal';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntities } from '../../manager/workflow-instancia-estado-nodos.reducer';
import {
  cantidadEstadoNodosPorEstado,
  findEstadoNodosResumenMetaPorEstado,
  findEstadoNodosResumenVencidoAgregado,
} from '../../shared/workflow/estado-nodos-resumen.utils';

const NODO_ESTADO_OPTS = [
  { value: 'NUEVO', label: 'Nuevo' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'GENERADO', label: 'Generado' },
  { value: 'EN_PROCESO', label: 'En Proceso' },
  { value: 'FINALIZADO', label: 'Finalizado' },
  { value: 'VENCIDO', label: 'Vencido' },
  { value: 'CANCELADO', label: 'Cancelado' },
  { value: 'BLOQUEADO', label: 'Bloqueado' },
];

const WF_ESTADO_OPTS = [
  { value: 'GENERADO', label: 'Generado' },
  { value: 'EN_PROCESO', label: 'En Proceso' },
  { value: 'EJECUCION', label: 'En ejecución' },
  { value: 'FINALIZADO', label: 'Finalizado' },
];

interface ActividadesFilterDef {
  label: string;
  name: string;
  single?: boolean;
  type?: 'select';
  options?: { value: string; label: string }[];
}

const MIS_ACTIVIDADES_FILTER: ActividadesFilterDef = {
  label: 'Mis actividades',
  name: 'misActividades',
  single: true,
  type: 'select' as const,
  options: [
    { value: 'S', label: 'Sí' },
    { value: 'N', label: 'No' },
  ],
};

const ACTIVIDADES_GRID_FILTERS: ActividadesFilterDef[] = [
  { label: 'Workflow', name: 'workflowNombre' },
  { label: 'Actividad', name: 'categoriaActividadDescripcion' },
  { label: 'Actividad ID', name: 'categoriaActividadId' },
  { label: 'RFC', name: 'empresaRfc' },
  { label: 'Razón social', name: 'empresaRazonSocial' },
  { label: 'Grupo', name: 'grupoAlias' },
  { label: 'Grupo ID', name: 'grupoId' },
  { label: 'Usuario', name: 'usuarioNombreCompleto' },
  { label: 'Usuario ID', name: 'usuarioId' },
  { label: 'Autoriza', name: 'autorizaNombreCompleto' },
  { label: 'Autoriza ID', name: 'autorizaId' },
  { label: 'Proceso', name: 'workflowInstanciaId' },
  { label: 'Workflow ID', name: 'workflowId' },
  { label: 'Empresa ID', name: 'empresaId' },
  {
    label: 'Estado',
    name: 'nodoEstado',
    type: 'select' as const,
    options: NODO_ESTADO_OPTS,
  },
  {
    label: 'Estado Proceso',
    name: 'workflowEstado',
    type: 'select' as const,
    options: WF_ESTADO_OPTS,
  },
  {
    label: 'Vencido',
    name: 'vencido',
    single: true,
    type: 'select' as const,
    options: [
      { value: 'true', label: 'Sí' },
      { value: 'false', label: 'No' },
    ],
  },
];

const ACTIVIDADES_FILTERS: ActividadesFilterDef[] = [MIS_ACTIVIDADES_FILTER, ...ACTIVIDADES_GRID_FILTERS];

interface NodoEstadoResumen {
  nodoEstado?: string | null;
  vencido?: boolean | null;
  cantidad: number;
  color?: string;
  porcentajeDelTotal?: number;
}

interface ResumenEstadoRow {
  nodoEstado: string;
  cantidad: number;
  color?: string;
  porcentajeDelTotal?: number;
}

interface ResumenVencidasRow {
  cantidad: number;
  color?: string;
  porcentajeDelTotal?: number;
}

function filtersFromSearch(
  search: string,
  filterDefs: ActividadesFilterDef[],
): { filterName: string; filterValue: string }[] {
  const urlParams = new URLSearchParams(search);
  return filterDefs.flatMap(f => {
    const values = urlParams.getAll(f.name).filter(v => v !== '');
    if (f.single) {
      const v = values.length > 0 ? values[values.length - 1] : null;
      return v != null ? [{ filterName: f.name, filterValue: v }] : [];
    }
    return values.map(v => ({ filterName: f.name, filterValue: v }));
  });
}

function buildEstadoNodosResumenParams(search: string): URLSearchParams {
  const params = new URLSearchParams();
  filtersFromSearch(search, ACTIVIDADES_FILTERS)
    .filter(f => f.filterName !== 'nodoEstado' && f.filterName !== 'vencido')
    .forEach(f => {
      params.append(f.filterName, f.filterValue);
    });
  return params;
}

function setNodoEstadoFilter(
  searchParams: URLSearchParams,
  estado: string,
): URLSearchParams {
  const params = new URLSearchParams(searchParams);
  params.delete('nodoEstado');
  params.set('nodoEstado', String(estado).toUpperCase());
  params.set('page', '1');
  return params;
}

function setVencidoFilter(
  searchParams: URLSearchParams,
  vencido: boolean,
): URLSearchParams {
  const params = new URLSearchParams(searchParams);
  params.delete('vencido');
  params.set('vencido', vencido ? 'true' : 'false');
  params.set('page', '1');
  return params;
}

const RESUMEN_ESTADOS_REQUERIDOS: { nodoEstado: string; color: string }[] = [
  { nodoEstado: 'BLOQUEADO', color: 'gray' },
  { nodoEstado: 'PENDIENTE', color: 'purple' },
  { nodoEstado: 'EN_PROCESO', color: 'blue' },
  { nodoEstado: 'FINALIZADO', color: 'green' },
];

const NODO_ESTADO_BADGE_HEX: Record<string, string> = {
  BLOQUEADO: '#94a3b8',
  PENDIENTE: '#8b5cf6',
  EN_PROCESO: '#3b82f6',
  FINALIZADO: '#22c55e',
};

const API_NODO_COLOR_TO_HEX: Record<string, string> = {
  green: '#22c55e',
  gray: '#94a3b8',
  grey: '#94a3b8',
  purple: '#8b5cf6',
  blue: '#3b82f6',
  red: '#ef4444',
  black: '#1e293b',
};

function resolveNodoEstadoBadgeColor(nodoEstado?: string, apiColor?: string | null): string {
  const up = String(nodoEstado ?? '').toUpperCase();
  if (up === 'FINALIZADO') {
    return NODO_ESTADO_BADGE_HEX.FINALIZADO;
  }
  if (!apiColor) return '#ef4444';
  const normalized = String(apiColor).trim().toLowerCase();
  if (API_NODO_COLOR_TO_HEX[normalized]) return API_NODO_COLOR_TO_HEX[normalized];
  if (/^#[0-9a-f]{3,8}$/i.test(String(apiColor).trim())) return String(apiColor).trim();
  return '#ef4444';
}

function normalizeResumenRows(rows: NodoEstadoResumen[]): {
  estadoRows: ResumenEstadoRow[];
  vencidas: ResumenVencidasRow;
} {
  const vencidasRow = findEstadoNodosResumenVencidoAgregado(rows);

  return {
    estadoRows: RESUMEN_ESTADOS_REQUERIDOS.map(({ nodoEstado, color }) => {
      const existing = findEstadoNodosResumenMetaPorEstado(rows, nodoEstado);
      return {
        nodoEstado,
        cantidad: cantidadEstadoNodosPorEstado(rows, nodoEstado),
        color: existing?.color ?? color,
        porcentajeDelTotal: existing?.porcentajeDelTotal,
      };
    }),
    vencidas: {
      cantidad: Number(vencidasRow?.cantidad) || 0,
      color: vencidasRow?.color ?? 'red',
      porcentajeDelTotal: vencidasRow?.porcentajeDelTotal,
    },
  };
}

const PROCESO_ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  EN_PROCESO: { label: 'En Proceso', bg: '#eff6ff', color: '#1d4ed8' },
};

function ProcesoEstadoCell({ estado, compact }: { estado?: string; compact?: boolean }) {
  if (!estado) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const cfg = PROCESO_ESTADO_CONFIG[estado.toUpperCase()];
  if (!cfg) return <span>{estado}</span>;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: compact ? '1px 6px' : '2px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: cfg.bg,
        color: cfg.color,
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
}

/** Build a usuario-shaped object for `UserInfo` from flat DTO fields. */
function usuarioFromFlat(id?: number | null, nombreCompleto?: string | null) {
  if (id == null) return null;
  return { id, nombre: nombreCompleto ?? '', apellido: '' };
}

function nodoEstadoLabel(estado?: string): string {
  if (!estado) return '';
  const up = estado.toUpperCase();
  const opt = NODO_ESTADO_OPTS.find(o => o.value === up);
  if (opt) return opt.label;
  if (up === 'EJECUCION') return 'En Proceso';
  return estado;
}

function nodoEstadoResumenLabelClass(estado: string): string {
  const up = estado.toUpperCase();
  if (up === 'FINALIZADO') {
    return 'procesos-estado-resumen__estado-label procesos-estado-resumen__estado-label--green';
  }
  return 'procesos-estado-resumen__estado-label';
}

function VencidasResumenLabel({ onClick }: { onClick?: () => void }) {
  const labelClass = 'procesos-estado-resumen__estado-label procesos-estado-resumen__estado-label--red';

  if (!onClick) {
    return <span className={labelClass}>Vencidas</span>;
  }

  return (
    <button
      type="button"
      className="procesos-estado-resumen__filter-btn"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      title="Filtrar por: Vencidas"
      aria-label="Filtrar por Vencidas"
    >
      <span className={labelClass}>Vencidas</span>
    </button>
  );
}

function NodoEstadoCell({
  estado,
  color,
  onClick,
  compact,
}: {
  estado?: string;
  color?: string;
  onClick?: () => void;
  compact?: boolean;
}) {
  if (!estado) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const border = color ? `2px solid ${color}` : '1px solid red';
  const badgeStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: compact ? '1px 6px' : '2px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    border,
    whiteSpace: 'nowrap',
    background: color,
    color: 'white',
  };

  const label = nodoEstadoLabel(estado);

  if (onClick) {
    return (
      <button
        type="button"
        className="actividades-estado-filter-btn"
        onClick={e => {
          e.stopPropagation();
          onClick();
        }}
        title={`Filtrar por: ${label}`}
        aria-label={`Filtrar por ${label}`}
      >
        <span style={badgeStyle}>{label}</span>
      </button>
    );
  }

  return <span style={badgeStyle}>{label}</span>;
}

function NodoEstadoResumenLabel({
  nodoEstado,
  onClick,
}: {
  nodoEstado: string;
  onClick?: () => void;
}) {
  const label = nodoEstadoLabel(nodoEstado);
  const labelClass = nodoEstadoResumenLabelClass(nodoEstado);

  if (!onClick) {
    return <span className={labelClass}>{label}</span>;
  }

  return (
    <button
      type="button"
      className="procesos-estado-resumen__filter-btn"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      title={`Filtrar por: ${label}`}
      aria-label={`Filtrar por ${label}`}
    >
      <span className={labelClass}>{label}</span>
    </button>
  );
}

function ActividadesEstadoNodosResumenBar({
  rows,
  loading,
  onEstadoClick,
  onVencidasClick,
}: {
  rows: NodoEstadoResumen[];
  loading: boolean;
  onEstadoClick: (estado: string) => void;
  onVencidasClick: () => void;
}) {
  const { estadoRows, vencidas } = normalizeResumenRows(rows);
  const total = estadoRows.reduce((sum, row) => sum + (Number(row.cantidad) || 0), 0);

  if (loading) {
    return (
      <div className="procesos-estado-resumen" role="status" aria-live="polite">
        <span className="procesos-estado-resumen__loading">Cargando resumen…</span>
      </div>
    );
  }

  const vencidasCantidad = Number(vencidas.cantidad) || 0;
  const vencidasPct = vencidas.porcentajeDelTotal != null
    ? Math.round(vencidas.porcentajeDelTotal)
    : total > 0
      ? Math.round((vencidasCantidad / total) * 100)
      : null;

  return (
    <div className="procesos-estado-resumen" role="status" aria-live="polite">
      {estadoRows.map(row => {
        const cantidad = Number(row.cantidad) || 0;
        const pct = row.porcentajeDelTotal != null
          ? Math.round(row.porcentajeDelTotal)
          : total > 0
            ? Math.round((cantidad / total) * 100)
            : null;

        return (
          <div key={row.nodoEstado} className="procesos-estado-resumen__estado">
            <NodoEstadoResumenLabel
              nodoEstado={row.nodoEstado}
              onClick={() => onEstadoClick(row.nodoEstado)}
            />
            <div className="procesos-estado-resumen__metricas">
              <span className="procesos-estado-resumen__valor">{cantidad}</span>
              <span className="procesos-estado-resumen__valor">
                {pct == null ? 'N/D' : `${pct}%`}
              </span>
            </div>
          </div>
        );
      })}
      <div className="procesos-estado-resumen__estado">
        <VencidasResumenLabel onClick={onVencidasClick} />
        <div className="procesos-estado-resumen__metricas">
          <span className="procesos-estado-resumen__valor">{vencidasCantidad}</span>
          <span className="procesos-estado-resumen__valor">
            {vencidasPct == null ? 'N/D' : `${vencidasPct}%`}
          </span>
        </div>
      </div>
      <div className="procesos-estado-resumen__total">
        <span className="procesos-estado-resumen__total-label">Total</span>
        <span className="procesos-estado-resumen__valor procesos-estado-resumen__valor--total">{total}</span>
      </div>
    </div>
  );
}

function fmtVencimiento(val?: string | null, opts?: { vencido?: boolean }) {
  if (!val) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  return (
    <DateFormat
      value={val}
      pattern={EMPRESA_VENCIMIENTO_DATE_PATTERN}
      highlight={opts?.vencido === true}
      expiredStyle={opts?.vencido ? ACTIVIDAD_VENCIDA_DATE_STYLE : undefined}
    />
  );
}

/** Default URL when opening /actividades with no query string. */
function buildDefaultActividadesSearchParams(): URLSearchParams {
  const p = new URLSearchParams();
  p.set('page', '1');
  p.set('sort', 'nodoEstado,asc');
  p.set('misActividades', 'S');
  p.set('vencido', 'true');
  p.append('nodoEstado', 'PENDIENTE');
  p.append('nodoEstado', 'BLOQUEADO');
  return p;
}

type ActividadesVista = 'mine' | 'all';

function ActividadesVistaToggle({
  value,
  onChange,
}: {
  value: ActividadesVista;
  onChange: (vista: ActividadesVista) => void;
}) {
  return (
    <div className="actividades-vista-toggle" role="group" aria-label="Vista">
      <div className="actividades-vista-toggle__options">
        <button
          type="button"
          className={`actividades-vista-toggle__btn${value === 'mine' ? ' is-active' : ''}`}
          aria-pressed={value === 'mine'}
          onClick={() => onChange('mine')}
        >
          Mis Actividades
        </button>
        <button
          type="button"
          className={`actividades-vista-toggle__btn${value === 'all' ? ' is-active' : ''}`}
          aria-pressed={value === 'all'}
          onClick={() => onChange('all')}
        >
          Todas las Actividades
        </button>
      </div>
    </div>
  );
}

function rowHasAdjuntos(row: Record<string, unknown>): boolean {
  if (row.tieneAdjuntos === true) return true;
  return row.archivoId != null;
}

function ActividadArchivoDownloadCell({
  onClick,
}: {
  onClick: () => void;
}) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <Tooltip title="Ver adjuntos" placement="top" arrow>
      <button
        type="button"
        className="actividades-archivo-download-btn"
        onClick={handleClick}
        aria-label="Ver adjuntos"
      >
        <FaPaperclip size={18} />
      </button>
    </Tooltip>
  );
}

export default function ActividadesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  // Apply defaults only for a completely empty query
  useLayoutEffect(() => {
    const q = location.search.replace(/^\?/, '').trim();
    if (q !== '') return;
    setSearchParams(buildDefaultActividadesSearchParams(), { replace: true });
  }, [location.pathname, location.search, setSearchParams]);

  const entities = useAppSelector(s => (s as any).workflowInstanciaEstadoNodos.entities);
  const totalItems = useAppSelector(s => (s as any).workflowInstanciaEstadoNodos.totalItems);
  const loading = useAppSelector(s => (s as any).workflowInstanciaEstadoNodos.loading);
  const errorMessage = useAppSelector(s => (s as any).workflowInstanciaEstadoNodos.errorMessage);

  const [rows, setRows] = useState<any[]>([]);
  const [estadoNodosResumen, setEstadoNodosResumen] = useState<NodoEstadoResumen[]>([]);
  const [resumenLoading, setResumenLoading] = useState(false);
  const [adjuntosModal, setAdjuntosModal] = useState<{
    nodoInstanciaId: number;
    title: string;
    context: ActividadAdjuntosListModalContext;
  } | null>(null);
  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) {
      showToast('success', msg);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRows(entities ?? []);
  }, [entities]);

  const fetchEstadoNodosResumen = useCallback(async () => {
    setResumenLoading(true);
    try {
      const params = buildEstadoNodosResumenParams(location.search);
      params.append('cacheBuster', String(Date.now()));
      const res = await axios.get<NodoEstadoResumen[]>(
        `api/profase-workflow-instancias/estado-nodos/resumen?${params.toString()}`,
      );
      setEstadoNodosResumen(Array.isArray(res.data) ? res.data : []);
    } catch {
      setEstadoNodosResumen([]);
    } finally {
      setResumenLoading(false);
    }
  }, [location.search]);

  useEffect(() => {
    fetchEstadoNodosResumen();
  }, [fetchEstadoNodosResumen]);

  const handleNodoEstadoFilter = useCallback((estado: string) => {
    setSearchParams(setNodoEstadoFilter(searchParams, estado));
  }, [searchParams, setSearchParams]);

  const handleVencidoFilter = useCallback(() => {
    setSearchParams(setVencidoFilter(searchParams, true));
  }, [searchParams, setSearchParams]);

  const fetchData = (params: any) => {
    const misActividades = searchParams.get('misActividades');
    const filters = misActividades
      ? [...params.filters, { filterName: 'misActividades', filterValue: misActividades }]
      : params.filters;
    dispatch(getEntities({ ...params, filters }));
  };

  const vista: ActividadesVista = searchParams.get('misActividades') === 'N' ? 'all' : 'mine';

  const handleVistaChange = (next: ActividadesVista) => {
    const params = new URLSearchParams(searchParams);
    params.set('misActividades', next === 'mine' ? 'S' : 'N');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleRefresh = useCallback(() => {
    void fetchEstadoNodosResumen();

    const params = new URLSearchParams(location.search);
    const page = Number(params.get('page') ?? 1);
    const sortParam = params.get('sort') ?? 'nodoEstado,asc';
    const filters = filtersFromSearch(location.search, ACTIVIDADES_FILTERS);

    dispatch(getEntities({
      page: page - 1,
      sort: sortParam,
      filters,
      size: ITEMS_PER_PAGE,
    }));
  }, [location.search, dispatch, fetchEstadoNodosResumen]);

  const handleOpenAdjuntosModal = useCallback((row: Record<string, unknown>) => {
    const nodoInstanciaId = row.id;
    if (nodoInstanciaId == null || nodoInstanciaId === '') return;
    const context = buildAdjuntosModalContextFromEstadoNodosRow(row);
    const actividad = context.actividad ?? 'Actividad';
    const empresa = context.razonSocial ?? context.rfc ?? '';
    const title = empresa ? `Adjuntos — ${actividad} (${empresa})` : `Adjuntos — ${actividad}`;
    setAdjuntosModal({
      nodoInstanciaId: Number(nodoInstanciaId),
      title,
      context,
    });
  }, []);

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    {
      field: 'workflowInstanciaId',
      headerName: 'Proceso',
      flex: 0.8,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.workflowInstanciaId != null ? String(p.row.workflowInstanciaId) : null}
          onClick={() => navigate(`/procesos?id=${p.row.workflowInstanciaId}`)}
        />
      ),
    },
    {
      field: 'workflowEstado',
      headerName: 'Estado Proceso',
      flex: 1.2,
      renderCell: (p: any) => <ProcesoEstadoCell estado={p.row.workflowEstado} compact />,
    },
    {
      field: 'workflowNombre',
      headerName: 'Workflow',
      flex: 2,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.workflowNombre ?? '—'}
          filterName="workflowId"
          filterValue={p.row.workflowId}
        />
      ),
    },
    {
      field: 'categoriaActividadDescripcion',
      headerName: 'Actividad',
      flex: 2,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.categoriaActividadDescripcion ?? null}
          filterName="categoriaActividadId"
          filterValue={p.row.categoriaActividadId}
        />
      ),
    },
    {
      field: 'empresaRfc',
      headerName: 'RFC',
      flex: 1.2,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.empresaRfc ?? null}
          filterName="empresaId"
          filterValue={p.row.empresaId}
        />
      ),
    },
    {
      field: 'empresaRazonSocial',
      headerName: 'Razón social',
      flex: 2.5,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.empresaRazonSocial ?? null}
          filterName="empresaId"
          filterValue={p.row.empresaId}
        />
      ),
    },
    {
      field: 'grupoAlias',
      headerName: 'Grupo',
      flex: 1.8,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.grupoAlias ?? null}
          filterName="grupoId"
          filterValue={p.row.grupoId}
        />
      ),
    },
    {
      field: 'usuarioId',
      headerName: 'Usuario',
      flex: 1.5,
      renderCell: (p: any) => (
        <UserInfo
          usuario={usuarioFromFlat(p.row.usuarioId, p.row.usuarioNombreCompleto)}
          onClick={u => {
            const next = new URLSearchParams(searchParams);
            next.set('usuarioId', String(u.id));
            next.set('page', '1');
            setSearchParams(next);
          }}
        />
      ),
    },
    {
      field: 'autorizaId',
      headerName: 'Autoriza',
      flex: 1.5,
      renderCell: (p: any) => (
        <UserInfo
          usuario={usuarioFromFlat(p.row.autorizaId, p.row.autorizaNombreCompleto)}
          onClick={u => {
            const next = new URLSearchParams(searchParams);
            next.set('autorizaId', String(u.id));
            next.set('page', '1');
            setSearchParams(next);
          }}
        />
      ),
    },
    {
      field: 'fechaVencimientoSAT',
      headerName: 'Venc. SAT',
      flex: 1.1,
      renderCell: (p: any) => fmtVencimiento(p.row.fechaVencimientoSAT ?? p.row.fechaVencimientoSat),
    },
    {
      field: 'fechaVencimientoProfase',
      headerName: 'Venc. Profase',
      flex: 1.1,
      renderCell: (p: any) => fmtVencimiento(p.row.fechaVencimientoProfase, { vencido: p.row.vencido === true }),
    },
    {
      field: 'nodoEstado',
      headerName: 'Estado',
      flex: 1.2,
      renderCell: (p: any) => (
        <NodoEstadoCell
          estado={p.row.nodoEstado}
          color={resolveNodoEstadoBadgeColor(p.row.nodoEstado, p.row.color)}
          onClick={() => handleNodoEstadoFilter(p.row.nodoEstado)}
          compact
        />
      ),
    },
    {
      field: 'adjuntos',
      headerName: '',
      flex: 0.4,
      sortable: false,
      cellClassName: 'actividades-archivo-cell',
      renderCell: (p: any) => {
        if (!rowHasAdjuntos(p.row)) return null;
        return (
          <ActividadArchivoDownloadCell
            onClick={() => handleOpenAdjuntosModal(p.row)}
          />
        );
      },
    },
  ];

  /** Filter `name` must match backend query parameter names (flat DTO fields). */
  const filters = ACTIVIDADES_GRID_FILTERS;

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Actividades', icon: <FaClipboardList /> },
        ]}
      />

      <PageHeader
        icon={<FaClipboardList />}
        iconColor="#3b82f6"
        title="Actividades"
        subtitle="Estado de nodos por instancia de workflow"
        action={{
          label: (
            <FaSync
              style={{
                fontSize: 14,
                animation: loading || resumenLoading ? 'spin 0.7s linear infinite' : undefined,
              }}
            />
          ),
          onClick: handleRefresh,
          disabled: loading || resumenLoading,
        }}
      />

      {adjuntosModal && (
        <ActividadAdjuntosListModal
          open
          title={adjuntosModal.title}
          context={adjuntosModal.context}
          nodoInstanciaId={adjuntosModal.nodoInstanciaId}
          onClose={() => setAdjuntosModal(null)}
        />
      )}

      {alert && <PageAlert alert={alert} />}
      {errorMessage && (
        <div style={g.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}

      <ActividadesVistaToggle value={vista} onChange={handleVistaChange} />

      <ActividadesEstadoNodosResumenBar
        rows={estadoNodosResumen}
        loading={resumenLoading}
        onEstadoClick={handleNodoEstadoFilter}
        onVencidasClick={handleVencidoFilter}
      />

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        clearFiltersSetParams={{ misActividades: 'N' }}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24, padding: '0 4px' },
  errorBanner: {
    padding: '10px 14px',
    background: '#fef2f2',
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 8,
    fontSize: 13,
  },
};
